/**
 * The visitor's country (ISO 3166-1 alpha-2), sent with the sign-up so the Salesforce lead
 * carries Lead.CountryCode and the HubSpot form is chosen by region. Two sources, in order:
 *   1. The sign-up link, when the marketing site appends the country: `country_code`,
 *      `countrycode` or `country`. It always wins over the lookup, so a link can force the
 *      experience of any country.
 *   2. HubSpot: the trial form's embed definition (what the HubSpot forms embed script loads)
 *      carries `countryCode`, the country HubSpot places the caller's address in. It answers
 *      any origin, and it is where reg.ssw.splashtop.com gets its country too.
 * Nothing is remembered between visits: HubSpot is asked again each time, so a visitor whose
 * network location changed (a VPN, a different office) is placed afresh. A miss sends no
 * country.
 */
import { HUBSPOT_PORTAL_ID, HUBSPOT_TRIAL_FORM_GUID } from './marketingAnalytics'

/** The link parameter, in the spellings marketing uses; the first one present wins. */
export const COUNTRY_CODE_PARAMS = ['country_code', 'countrycode', 'country'] as const
export const GEO_LOOKUP_URL = `https://forms.hsforms.com/embed/v3/form/${HUBSPOT_PORTAL_ID}/${HUBSPOT_TRIAL_FORM_GUID}/json`
const LOOKUP_TIMEOUT_MS = 2500

function normalise(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const code = value.trim().toUpperCase()
  return /^[A-Z]{2}$/.test(code) ? code : undefined
}

async function lookup(): Promise<string | undefined> {
  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), LOOKUP_TIMEOUT_MS)
  try {
    const response = await fetch(GEO_LOOKUP_URL, { signal: controller.signal, cache: 'no-store' })
    if (!response.ok) return undefined
    const body = await response.json()
    return normalise(body?.countryCode)
  } catch {
    return undefined
  } finally {
    window.clearTimeout(timer)
  }
}

/** Resolve the visitor's country from the link, else from HubSpot. */
export async function resolveCountryCode(search: string): Promise<string | undefined> {
  const params = new URLSearchParams(search)
  for (const name of COUNTRY_CODE_PARAMS) {
    const fromLink = normalise(params.get(name))
    if (fromLink) return fromLink
  }
  return lookup()
}
